<script setup>
import { createValidationRules } from "@/utils/validationRules"
import { useI18n } from "vue-i18n"
import { useEventStore } from "@/stores/EventsStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { blockMinusInput, formatNumber } from "@/utils/strings"
import { showWarningToast } from "@/utils/toast.js"
import { toRaw } from 'vue'

const props = defineProps({
  isInSales: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['amountExceeded']) //amount alarm
const eventStore = useEventStore()
const calendarStore = useCalendarStore()

const { t } = useI18n()

//const autocompleteRef = ref(null)
//const localSelectedPaymentMethods = ref(null)

// Object for handling selection/deselection of payment methods
// even when they are disabled
const paymentMethods = computed(() =>
  calendarStore.paymentMethods
    .filter(method => {
      const alreadySelected = eventStore.selectedPaymentMethods.some(selected => selected.name === method.name && !['bon_saloner', 'pass_saloner'].includes(selected.name))
      if (alreadySelected) return false
      if (props.isInSales) {
        return (method.enabled && method.checkout && method.sales) || (method.id !== 0 && method.id === eventStore.selectedPrepaymentMethod.id)
      } else {
        return (method.enabled && method.checkout) || (method.id !== 0 && method.id === eventStore.selectedPrepaymentMethod.id)
      }
    })
    //.map(method => ({ ...method, amount: 0, changedByHuman: false })),
    .map(method => ({
      ...structuredClone(toRaw(method)),
      amount: null,
      changedByHuman: false,
    })),
)

const checkCode = (method) => {
  if(method.voucher?.identifier.length > 0 && method.voucher?.identifier.length !== 6) {
    console.log('check code', method)
    method.voucher.checkStatus = 0
    method.voucher.statusMessage = t('type_6_voucher_code')
    method.voucher.availableValue = 0
    method.voucher.services = []
  }
}

const verifyVoucher = async (code, method, index) => {
  method.voucher.checkStatus = 0
  method.voucher.statusMessage = ''
  method.voucher.availableValue = 0
  method.voucher.services = []

  if(code.length !== 6) {


    return
  }
  try {
    method.voucher.checkStatus = 1
    const response = await $backend(`/productSales/verifyVoucher?n=${method.name}`, { // pass n param so checking pass code wont abort voucher check and vice versa
      method: 'POST',
      body: {
        "name": method.name,
        "couponCode": code,
      },
    })

    if(response.success && response.data.isValid) {
      method.voucher = {
        ...response.data.voucher,
        checkStatus: 2,
        statusMessage: '',
      }
      await fillAmountsInSelectedPaymentMethods()
      // let serviceId = Number(method.voucher.services[0].serviceId)
      // let service = eventStore.selectedServices.find(s => s.id === serviceId)
      // method.amount = service.finalPrice
    } else {
      method.voucher.checkStatus = 3
      method.voucher.statusMessage = response.data.message || t('incorrect_code')
      method.voucher.availableValue = 0
      method.amount = null
      method.voucher.services = []
    }
  } catch (err) {
    method.voucher.checkStatus = 3
    method.voucher.statusMessage = t('incorrect_code')
    method.voucher.availableValue = 0
    method.voucher.services = []
  } finally {
    if(method.voucher.checkStatus === 1) method.voucher.checkStatus = 0
    nextTick(() => {
      // B: nie dziala ref za dobrze w tym przypadku przez petle for - do sprawdzenia w przyszlosci
      document.getElementById(`${method.name}_input_${index}`)?.focus()
    })
  }
}

// Voucher status icons
const voucherStatusIcon = status => {
  switch (status) {
  case 0: return ""    // to check
  case 1: return "" // checking
  case 2: return "ri-check-line"   // ok
  case 3: return "ri-prohibited-line"   // error
  //case 20: return "ri-check-double-line"   // error
  default: return ""
  }
}

// Validation Rules
const validationRules = createValidationRules(t)

onMounted(() => {
  //Add default payment method
  if(eventStore.selectedPaymentMethods.length === 0 && eventStore.allowSave) {
    const firstAvailable = paymentMethods.value.find(method => method.enabled && method.checkout)
    if (firstAvailable) {
      eventStore.selectedPaymentMethods = [{
        ...firstAvailable,
        amount: eventStore.balanceAmount<0 ? Math.abs(eventStore.balanceAmount) : 0,
        changedByHuman: false,
      }]
    }
  }
  setTimeout(() => { // B: let it some time (eg. for animation) before we start looking for changes
    eventStore.isWatcherActive = true
  }, 1000)

})

const usePaymentMethod = async () => {
  //if(!method) return

  let method = paymentMethods.value.find(pm =>
    !eventStore.selectedPaymentMethods.some(sel => sel.id === pm.id),
  )
  if (!method) { // if all methods are already selected ....
    method = paymentMethods.value[0]
  }

  let tmpMethod = JSON.parse(JSON.stringify(method))
  tmpMethod.amount = null
  if(['bon_saloner', 'pass_saloner'].includes(tmpMethod.name)) {
    eventStore.selectedPaymentMethods.push(tmpMethod)
  } else {
    if(eventStore.selectedPaymentMethods.find(m => m.name === tmpMethod.name)) showWarningToast(t('this_payment_method_can_be_added_once'))
    else eventStore.selectedPaymentMethods.push(tmpMethod)
  }
  fillAmountsInSelectedPaymentMethods()

  // await nextTick(() => {
  //   localSelectedPaymentMethods.value = null
  //   //autocompleteRef.value?.blur()
  // })

}

const removePaymentMethod = async (index) => {
  if (index >= 0 && index < eventStore.selectedPaymentMethods.length) {
    eventStore.selectedPaymentMethods.splice(index, 1)
    await fillAmountsInSelectedPaymentMethods()
  }
}

const fillAmountsInSelectedPaymentMethods = async () => {
  //console.warn('fillAmountsInSelectedPaymentMethods', eventStore.selectedPaymentMethods[0])
  await nextTick()
  if(!eventStore.allowSave) return
  // Change selected payment method amount to match the balance amount
  for(let i = 0; i < eventStore.selectedPaymentMethods.length; i++) {
    if(!eventStore.selectedPaymentMethods[i].changedByHuman) {
      if(eventStore.selectedPaymentMethods[i].name === 'pass_saloner') continue
      let currentBalance = eventStore.balanceAmount
      eventStore.selectedPaymentMethods[i].amount -= currentBalance
      if(eventStore.selectedPaymentMethods[i].amount <= 0) eventStore.selectedPaymentMethods[i].amount = null
      // check if VOUCHER availableValue is enough
      if(eventStore.selectedPaymentMethods[i].voucher && Number(eventStore.selectedPaymentMethods[i].voucher.availableValue) < eventStore.selectedPaymentMethods[i].amount) {
        eventStore.selectedPaymentMethods[i].amount = Number(eventStore.selectedPaymentMethods[i].voucher.availableValue)
      }
      eventStore.selectedPaymentMethods[i].amount = Math.round(eventStore.selectedPaymentMethods[i].amount * 100) / 100
    }
  }

  // Reset amounts for all payment methods not selected
  paymentMethods.value.forEach(method => {
    const isSelected = eventStore.selectedPaymentMethods.some(selected => selected.name === method.name)
    if (!isSelected) {
      method.amount = null
      method.changedByHuman = false
      if(method.voucher) { // Also reset voucher's stuff
        method.voucher = {
          checkStatus: 0,
          statusMessage: '',
          identifier: '',
        }
      }
    }
  })

}

watch(
  [() => eventStore.prepaymentMethodTotal, () => eventStore.finalPrice],
  () => {
    if (eventStore.isWatcherActive && eventStore.allowSave) {
      fillAmountsInSelectedPaymentMethods()
    }
  },
)


onBeforeUnmount(() => {
  // Reset selected payment options
  eventStore.selectedPaymentMethods = []
})
</script>

<template>
  <VRow class="payment-row ">
    <!--    <VCol-->
    <!--      class="payment-navigation "-->
    <!--      cols="12"-->
    <!--    >-->
    <!--      <VAutocomplete-->
    <!--        ref="autocompleteRef"-->
    <!--        v-model="localSelectedPaymentMethods"-->
    <!--        class="mt-4"-->
    <!--        density="compact"-->
    <!--        color="secondary"-->
    <!--        :label="$t('payment_method')"-->
    <!--        :disabled="!eventStore.allowSave"-->
    <!--        :items="paymentMethods"-->
    <!--        item-title="title"-->
    <!--        item-value="name"-->
    <!--        return-object-->
    <!--        auto-select-first-->
    <!--        :no-data-text="$t('no_results')"-->
    <!--        @update:model-value="usePaymentMethod($event)"-->
    <!--      />-->
    <!--    </VCol>-->

    <!-- Selected Payment Method Details -->
    <VCol cols="12">
      <div>
        <VForm>
          <!-- Render Payment Fields -->
          <VSheet
            v-for="(method, index) in eventStore.selectedPaymentMethods"
            :key="`${method.name}_${index}`"
            class="py-2 px-0"
            flat
          >
            <VRow
              v-if="method.id !== 7 && method.id !== 9"
              dense
              class="border-bottom-dashed"
            >
              <VCol :cols="eventStore.allowSave ? 6 : 6">
                <VSelect
                  v-model="eventStore.selectedPaymentMethods[index]"
                  :items="paymentMethods"
                  item-title="title"
                  item-value="name"
                  return-object
                  :disabled="!eventStore.allowSave"
                  variant="outlined"
                  density="compact"
                  color="secondary"
                  :label="$t('payment_method')"
                  hide-details
                  @update:model-value="fillAmountsInSelectedPaymentMethods"
                />
              </VCol>
              <VCol :cols="eventStore.allowSave ? 4 : 6">
                <VNumberInput
                  v-model="method.amount"
                  :disabled="!eventStore.allowSave"
                  variant="outlined"
                  density="compact"
                  color="secondary"
                  :label="$t('value')"
                  placeholder="0"
                  :suffix="method.name === 'voucher' ? '' : calendarStore.currency"
                  class="mb-0 ml-auto"
                  control-variant="hidden"
                  maxlength="8"
                  :min="0"
                  :max="99999"
                  :precision="2"
                  :min-fraction-digits="0"
                  hide-details
                  @beforeinput="blockMinusInput"
                  @input="method.changedByHuman = true;"
                />
              </VCol>
              <VCol
                v-if="eventStore.allowSave"
                cols="2"
                class="d-flex align-center justify-end"
              >
                <VBtn
                  size="small"
                  color="gray"
                  variant="text"
                  icon="ri-delete-bin-line"
                  @click.stop="removePaymentMethod(index)"
                />
                <VTooltip activator="parent">
                  {{ $t('delete') }}
                </VTooltip>
              </VCol>
            </VRow>

            <template v-if="method.id === 7 && method.voucher">
              <!--BON SALONER-->
              <VRow
                dense
                class="border-bottom-dashed"
              >
                <VCol cols="12">
                  <VSelect
                    v-model="eventStore.selectedPaymentMethods[index]"
                    :items="paymentMethods"
                    item-title="title"
                    item-value="name"
                    return-object
                    :disabled="!eventStore.allowSave"
                    variant="outlined"
                    density="compact"
                    color="secondary"
                    :label="$t('payment_method')"
                    hide-details
                    @update:model-value="fillAmountsInSelectedPaymentMethods"
                  />
                </VCol>
                <VCol :cols="eventStore.allowSave ? 5 : 6">
                  <VTextField
                    :id="`${method.name}_input_${index}`"
                    v-model="method.voucher.identifier"
                    :disabled="!eventStore.allowSave || method.voucher.checkStatus === 1"
                    :loading="method.voucher.checkStatus === 1"
                    variant="outlined"
                    density="compact"
                    color="secondary"
                    :label="t('voucher_code')"
                    class="voucher-icon input-uppercase-text"
                    :class="method.voucher.checkStatus === 3 ? `voucher-error` : ``"
                    maxlength="6"
                    :append-inner-icon="voucherStatusIcon(method.voucher?.checkStatus)"
                    @input="(event) => verifyVoucher(event.target.value, method, index)"
                    @change="checkCode(method)"
                  />
                </VCol>
                <VCol :cols="eventStore.allowSave ? 5 : 6">
                  <VNumberInput
                    v-model="method.amount"
                    :disabled="!eventStore.allowSave || method.voucher.checkStatus === 1"
                    variant="outlined"
                    density="compact"
                    color="secondary"
                    placeholder="0"
                    :label="t('value')"
                    class="mb-0 "
                    control-variant="hidden"
                    :suffix="calendarStore.currency"
                    maxlength="8"
                    :min="0"
                    :max="Number(method.voucher.availableValue) || 99999"
                    :precision="2"
                    :min-fraction-digits="0"
                    hide-details
                    @beforeinput="blockMinusInput"
                    @input="method.changedByHuman = true;"
                  />
                  <small
                    v-if="method.voucher"
                    class="d-flex justify-end"
                  >
                    {{ method.voucher?.availableValue ? `${t('availableFromPlural')} ${formatNumber(method.voucher?.availableValue)}&nbsp;${calendarStore.currency}` : '' }}
                  </small>
                </VCol>
                <VCol
                  v-if="eventStore.allowSave"
                  cols="2"
                  class="d-flex align-top justify-end pt-2"
                >
                  <VBtn
                    size="small"
                    color="gray"
                    variant="text"
                    icon="ri-delete-bin-line"
                    @click.stop="removePaymentMethod(index)"
                  />
                  <VTooltip activator="parent">
                    {{ $t('delete') }}
                  </VTooltip>
                </VCol>
                <VCol
                  v-if="[0,3].includes(method.voucher?.checkStatus) && method.voucher?.statusMessage?.length > 1"
                  cols="12"
                  class="pt-0 pl-4"
                >
                  <small class="text-error">{{ method.voucher?.statusMessage }}</small>
                </VCol>
              </VRow>
            </template>
            <template v-if="method.id === 9 && method.voucher">
              <!--KARNET-->
              <VRow
                dense
                class="border-bottom-dashed"
              >
                <VCol :cols="eventStore.allowSave ? 6 : 6">
                  <VSelect
                    v-model="eventStore.selectedPaymentMethods[index]"
                    :items="paymentMethods"
                    item-title="title"
                    item-value="name"
                    return-object
                    :disabled="!eventStore.allowSave"
                    variant="outlined"
                    density="compact"
                    color="secondary"
                    :label="$t('payment_method')"
                    hide-details
                    @update:model-value="fillAmountsInSelectedPaymentMethods"
                  />
                </VCol>
                <VCol :cols="eventStore.allowSave ? 4 : 6">
                  <VTextField
                    :id="`${method.name}_input_${index}`"
                    v-model="method.voucher.identifier"
                    :disabled="!eventStore.allowSave || method.voucher.checkStatus === 1"
                    :loading="method.voucher.checkStatus === 1"
                    variant="outlined"
                    density="compact"
                    color="secondary"
                    :label="t('pass_code')"
                    class="voucher-icon input-uppercase-text"
                    :class="method.voucher.checkStatus === 3 ? `voucher-error` : ``"
                    maxlength="6"
                    :append-inner-icon="voucherStatusIcon(method.voucher?.checkStatus)"
                    @input="(event) => verifyVoucher(event.target.value, method, index)"
                    @change="checkCode(method)"
                  />
                </VCol>
                <VCol
                  v-if="eventStore.allowSave"
                  cols="2"
                  class="d-flex align-center justify-end"
                >
                  <VBtn
                    size="small"
                    color="gray"
                    variant="text"
                    icon="ri-delete-bin-line"
                    @click.stop="removePaymentMethod(index)"
                  />
                  <VTooltip activator="parent">
                    {{ $t('delete') }}
                  </VTooltip>
                </VCol>
                <VCol
                  v-if="method.voucher?.checkStatus === 2 && method.voucher?.identifier?.length > 1 && !method.used"
                  cols="12"
                  class="pt-0 pl-4"
                >
                  <small class="text-error">{{ $t('servicesNotCoveredByPass') }}</small>
                </VCol>
                <VCol
                  v-if="method.voucher?.checkStatus !== 2 && method.voucher?.identifier?.length > 1"
                  cols="12"
                  class="pt-0 pl-4"
                >
                  <small class="text-error">{{ method.voucher?.statusMessage }}</small>
                </VCol>
                <VCol
                  v-if="method.voucher?.services?.length > 0"
                  cols="12"
                  class="mt-0 pt-0"
                >
                  <ul class="list-none">
                    <li
                      v-for="(service, index) in method.voucher.services"
                      :key="`${index}_${service.id}`"
                      class="pl-3"
                    >
                      <small class="d-flex align-center">
                        <VIcon
                          icon="ri-corner-down-right-line"
                          class="mr-1"
                        />
                        <span class="line-height-normal">{{ service.serviceName }} x {{ service.usesLeft }}</span>
                      </small>
                    </li>
                  </ul>
                </VCol>
              </VRow>
            </template>
          </VSheet>
        </VForm>
      </div>
    </VCol>
    <VCol
      v-if="eventStore.allowSave"
      class="payment-navigation mt-0 pt-0 mb-2"
      cols="12"
    >
      <VBtn
        :disabled="eventStore.isSaving"
        class="w-100"
        variant="flat"
        color="secondary"
        @click="usePaymentMethod"
      >
        {{ $t('add_another_payment_method') }}
      </VBtn>
    </VCol>
  </VRow>
</template>

<style scoped>
.v-form .v-sheet:not(:last-child) {
  .border-bottom-dashed {
      border-bottom: 1px dashed rgb(var(--v-border-color), 0.2);
      padding-bottom: 4px!important;
  }
}

::v-deep(.voucher-icon .v-icon) {
  color: green;
}

::v-deep(.voucher-icon.voucher-error .v-icon) {
  color: red !important;
}
</style>


